import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/Layout';
import {
  assessmentsService,
  AssessmentInterviewQuestion,
  AssessmentScorecardStatement,
  ScorecardSubject,
} from '../api/assessmentsService';

const sortQuestions = (questions: AssessmentInterviewQuestion[]) =>
  [...questions].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);

const sortStatements = (statements: AssessmentScorecardStatement[]) =>
  [...statements].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);

const subjectOptions: Array<{ value: ScorecardSubject; label: string }> = [
  { value: 'candidate', label: 'Candidate' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'couple', label: 'Couple (husband & wife rated separately)' },
];

const AssessmentInterviewGuideDetail: React.FC = () => {
  const { guideId: guideIdParam } = useParams();
  const guideId = useMemo(() => parseInt(guideIdParam || '0', 10), [guideIdParam]);
  const queryClient = useQueryClient();

  const guideQuery = useQuery({
    queryKey: ['admin', 'interviewGuides', guideId],
    queryFn: () => assessmentsService.getInterviewGuide(guideId),
    enabled: Number.isFinite(guideId) && guideId > 0,
  });

  const [newSection, setNewSection] = useState('');
  const [newPrompt, setNewPrompt] = useState('');
  const [newHelpText, setNewHelpText] = useState('');
  const [newStatement, setNewStatement] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const guide = guideQuery.data?.data;
  const ordered = useMemo(() => sortQuestions(guide?.questions || []), [guide]);
  const sections = useMemo(() => {
    const grouped: Array<{ section: string | null; questions: AssessmentInterviewQuestion[] }> = [];
    const indexBySection = new Map<string, number>();
    for (const question of ordered) {
      const key = question.section || '';
      if (!indexBySection.has(key)) {
        indexBySection.set(key, grouped.length);
        grouped.push({ section: question.section || null, questions: [] });
      }
      grouped[indexBySection.get(key)!].questions.push(question);
    }
    return grouped;
  }, [ordered]);
  const existingSections = useMemo(
    () => Array.from(new Set(ordered.map((question) => question.section).filter(Boolean))) as string[],
    [ordered],
  );
  const statements = useMemo(() => sortStatements(guide?.scorecardStatements || []), [guide]);

  const onMutationError = (error: any) => {
    setErrorMessage(error?.response?.data?.message || 'Something went wrong.');
  };

  const invalidate = async () => {
    setErrorMessage(null);
    await queryClient.invalidateQueries({ queryKey: ['admin', 'interviewGuides'] });
  };

  const updateGuideMutation = useMutation({
    mutationFn: (payload: any) => assessmentsService.updateInterviewGuide(guideId, payload),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  const createQuestionMutation = useMutation({
    mutationFn: () =>
      assessmentsService.createInterviewQuestion(guideId, {
        section: newSection.trim() || null,
        prompt: newPrompt.trim(),
        helpText: newHelpText.trim() || null,
      }),
    onSuccess: async () => {
      setNewPrompt('');
      setNewHelpText('');
      await invalidate();
    },
    onError: onMutationError,
  });

  const updateQuestionMutation = useMutation({
    mutationFn: (vars: { questionId: number; payload: any }) =>
      assessmentsService.updateInterviewQuestion(guideId, vars.questionId, vars.payload),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  const deleteQuestionMutation = useMutation({
    mutationFn: (questionId: number) => assessmentsService.deleteInterviewQuestion(guideId, questionId),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  const reorderMutation = useMutation({
    mutationFn: (questions: Array<{ id: number; sortOrder: number }>) =>
      assessmentsService.reorderInterviewQuestions(guideId, questions),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  const createStatementMutation = useMutation({
    mutationFn: () =>
      assessmentsService.createScorecardStatement(guideId, { statement: newStatement.trim() }),
    onSuccess: async () => {
      setNewStatement('');
      await invalidate();
    },
    onError: onMutationError,
  });

  const updateStatementMutation = useMutation({
    mutationFn: (vars: { statementId: number; payload: any }) =>
      assessmentsService.updateScorecardStatement(guideId, vars.statementId, vars.payload),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  const deleteStatementMutation = useMutation({
    mutationFn: (statementId: number) => assessmentsService.deleteScorecardStatement(guideId, statementId),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  const reorderStatementsMutation = useMutation({
    mutationFn: (payload: Array<{ id: number; sortOrder: number }>) =>
      assessmentsService.reorderScorecardStatements(guideId, payload),
    onSuccess: invalidate,
    onError: onMutationError,
  });

  const moveStatement = (statementId: number, direction: -1 | 1) => {
    const index = statements.findIndex((item) => item.id === statementId);
    const target = statements[index + direction];
    if (index === -1 || !target) return;

    const next = [...statements];
    [next[index], next[index + direction]] = [next[index + direction], next[index]];
    reorderStatementsMutation.mutate(next.map((item, i) => ({ id: item.id, sortOrder: (i + 1) * 10 })));
  };

  // Moves a question within its own section so section blocks stay together.
  const moveQuestion = (questionId: number, direction: -1 | 1) => {
    const question = ordered.find((item) => item.id === questionId);
    if (!question) return;

    const sectionKey = question.section || '';
    const siblings = ordered.filter((item) => (item.section || '') === sectionKey);
    const siblingIndex = siblings.findIndex((item) => item.id === questionId);
    const target = siblings[siblingIndex + direction];
    if (!target) return;

    const next = [...ordered];
    const fromIndex = next.findIndex((item) => item.id === questionId);
    const toIndex = next.findIndex((item) => item.id === target.id);
    [next[fromIndex], next[toIndex]] = [next[toIndex], next[fromIndex]];

    reorderMutation.mutate(next.map((item, index) => ({ id: item.id, sortOrder: (index + 1) * 10 })));
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{guide?.title || 'Interview Guide'}</h1>
              <p className="text-gray-600 mt-1">
                {guide?.role ? `Used for ${guide.role.displayName}` : 'Not linked to an interview area'}
              </p>
            </div>
            <Link to="/interview-guides" className="text-sm text-primary-600 hover:text-primary-700">
              Back to Interview Guides
            </Link>
          </div>
        </div>

        {errorMessage && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {errorMessage}
          </div>
        )}

        {guideQuery.isLoading && (
          <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 text-gray-700">Loading guide…</div>
        )}

        {guide && (
          <>
            <div className="bg-white shadow-lg rounded-xl p-4 sm:p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">Guide Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    defaultValue={guide.title}
                    key={`title-${guide.id}-${guide.title}`}
                    onBlur={(e) => {
                      if (e.target.value.trim() && e.target.value !== guide.title) {
                        updateGuideMutation.mutate({ title: e.target.value.trim() });
                      }
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                  <input
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="e.g. Candidate Couple"
                    defaultValue={guide.subtitle || ''}
                    key={`subtitle-${guide.id}-${guide.subtitle || ''}`}
                    onBlur={(e) => {
                      if (e.target.value !== (guide.subtitle || '')) {
                        updateGuideMutation.mutate({ subtitle: e.target.value });
                      }
                    }}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Opening instructions</label>
                <p className="text-xs text-gray-500 mb-1">Shown to the assessor above the questions.</p>
                <textarea
                  className="w-full min-h-[90px] border border-gray-300 rounded-md px-3 py-2 text-sm"
                  defaultValue={guide.intro || ''}
                  key={`intro-${guide.id}-${guide.intro || ''}`}
                  onBlur={(e) => {
                    if (e.target.value !== (guide.intro || '')) {
                      updateGuideMutation.mutate({ intro: e.target.value });
                    }
                  }}
                />
              </div>
            </div>

            <div className="bg-white shadow-lg rounded-xl p-4 sm:p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">Add Question</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="Section (optional)"
                  list="interview-guide-sections"
                  value={newSection}
                  onChange={(e) => setNewSection(e.target.value)}
                />
                <datalist id="interview-guide-sections">
                  {existingSections.map((section) => (
                    <option key={section} value={section} />
                  ))}
                </datalist>
                <input
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm md:col-span-2"
                  placeholder="Question prompt"
                  value={newPrompt}
                  onChange={(e) => setNewPrompt(e.target.value)}
                />
              </div>
              <input
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Help text / qualifier (optional)"
                value={newHelpText}
                onChange={(e) => setNewHelpText(e.target.value)}
              />
              <button
                className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                disabled={!newPrompt.trim() || createQuestionMutation.isPending}
                onClick={() => createQuestionMutation.mutate()}
              >
                Add Question
              </button>
            </div>

            <div className="bg-white shadow-lg rounded-xl p-4 sm:p-6 space-y-6">
              <h2 className="text-lg font-semibold text-gray-900">Questions ({ordered.length})</h2>

              {ordered.length === 0 && (
                <div className="text-sm text-gray-600">No questions yet.</div>
              )}

              {sections.map((section, sectionIndex) => (
                <div key={section.section || `section-${sectionIndex}`} className="space-y-3">
                  <input
                    className="w-full md:w-80 border border-gray-300 rounded-md px-2 py-1 text-sm font-semibold text-primary-700"
                    placeholder="No section"
                    defaultValue={section.section || ''}
                    key={`section-name-${section.section || sectionIndex}`}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value === (section.section || '')) return;
                      section.questions.forEach((question) => {
                        updateQuestionMutation.mutate({
                          questionId: question.id,
                          payload: { section: value || null },
                        });
                      });
                    }}
                  />
                  {section.questions.map((question, questionIndex) => (
                    <div
                      key={question.id}
                      className={[
                        'rounded-lg border px-3 py-3 space-y-2',
                        question.isActive ? 'border-gray-200' : 'border-gray-200 bg-gray-50 opacity-70',
                      ].join(' ')}
                    >
                      <textarea
                        className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                        rows={2}
                        defaultValue={question.prompt}
                        key={`prompt-${question.id}-${question.prompt}`}
                        onBlur={(e) => {
                          if (e.target.value.trim() && e.target.value !== question.prompt) {
                            updateQuestionMutation.mutate({
                              questionId: question.id,
                              payload: { prompt: e.target.value.trim() },
                            });
                          }
                        }}
                      />
                      <input
                        className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm text-gray-600"
                        placeholder="Help text (optional)"
                        defaultValue={question.helpText || ''}
                        key={`help-${question.id}-${question.helpText || ''}`}
                        onBlur={(e) => {
                          if (e.target.value !== (question.helpText || '')) {
                            updateQuestionMutation.mutate({
                              questionId: question.id,
                              payload: { helpText: e.target.value },
                            });
                          }
                        }}
                      />
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                          <button
                            className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40"
                            disabled={questionIndex === 0 || reorderMutation.isPending}
                            onClick={() => moveQuestion(question.id, -1)}
                          >
                            Up
                          </button>
                          <button
                            className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40"
                            disabled={questionIndex === section.questions.length - 1 || reorderMutation.isPending}
                            onClick={() => moveQuestion(question.id, 1)}
                          >
                            Down
                          </button>
                        </div>
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={question.isActive}
                              onChange={(e) =>
                                updateQuestionMutation.mutate({
                                  questionId: question.id,
                                  payload: { isActive: e.target.checked },
                                })
                              }
                            />
                            Active
                          </label>
                          <button
                            className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
                            disabled={deleteQuestionMutation.isPending}
                            onClick={() => {
                              if (window.confirm('Delete this question? Any notes assessors saved against it are removed too.')) {
                                deleteQuestionMutation.mutate(question.id);
                              }
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="bg-white shadow-lg rounded-xl p-4 sm:p-6 space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Scorecard ({statements.length})</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Statements assessors rate from Strongly Disagree to Strongly Agree on the Scorecard tab, between the
                  interview guide and the written report.
                </p>
              </div>

              <div className="md:w-96">
                <label className="block text-sm font-medium text-gray-700 mb-1">Who is rated</label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  value={guide.scorecardSubject}
                  onChange={(e) =>
                    updateGuideMutation.mutate({ scorecardSubject: e.target.value as ScorecardSubject })
                  }
                >
                  {subjectOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <input
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="Statement (e.g. Candidate is actively involved in developing other leaders.)"
                  value={newStatement}
                  onChange={(e) => setNewStatement(e.target.value)}
                />
                <button
                  className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                  disabled={!newStatement.trim() || createStatementMutation.isPending}
                  onClick={() => createStatementMutation.mutate()}
                >
                  Add Statement
                </button>
              </div>

              {statements.length === 0 && (
                <div className="text-sm text-gray-600">No scorecard statements yet.</div>
              )}

              <div className="space-y-3">
                {statements.map((statement, index) => (
                  <div
                    key={statement.id}
                    className={[
                      'rounded-lg border px-3 py-3 space-y-2',
                      statement.isActive ? 'border-gray-200' : 'border-gray-200 bg-gray-50 opacity-70',
                    ].join(' ')}
                  >
                    <textarea
                      className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                      rows={2}
                      defaultValue={statement.statement}
                      key={`statement-${statement.id}-${statement.statement}`}
                      onBlur={(e) => {
                        if (e.target.value.trim() && e.target.value !== statement.statement) {
                          updateStatementMutation.mutate({
                            statementId: statement.id,
                            payload: { statement: e.target.value.trim() },
                          });
                        }
                      }}
                    />
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2">
                        <button
                          className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40"
                          disabled={index === 0 || reorderStatementsMutation.isPending}
                          onClick={() => moveStatement(statement.id, -1)}
                        >
                          Up
                        </button>
                        <button
                          className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40"
                          disabled={index === statements.length - 1 || reorderStatementsMutation.isPending}
                          onClick={() => moveStatement(statement.id, 1)}
                        >
                          Down
                        </button>
                      </div>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={statement.isActive}
                            onChange={(e) =>
                              updateStatementMutation.mutate({
                                statementId: statement.id,
                                payload: { isActive: e.target.checked },
                              })
                            }
                          />
                          Active
                        </label>
                        <button
                          className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
                          disabled={deleteStatementMutation.isPending}
                          onClick={() => {
                            if (window.confirm('Delete this statement? Any ratings assessors saved against it are removed too.')) {
                              deleteStatementMutation.mutate(statement.id);
                            }
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default AssessmentInterviewGuideDetail;
